import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';

/**
 * Tiny bilingual (EN / Bahasa Melayu) helper for PUBLIC pages — the draw
 * entry page and consent capture. The authenticated app UI is English.
 */

export type Lang = 'en' | 'ms';

export interface LangPair {
  en: string;
  ms: string;
}

interface LangContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (pair: LangPair) => string;
}

const STORAGE_KEY = 'pdpo-go-lang';

const LangContext = createContext<LangContextType | undefined>(undefined);

function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ms' || stored === 'en') return stored;
  } catch {
    // storage unavailable — default to English
  }
  return 'en';
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable — language just won't persist
    }
  }, []);

  const t = useCallback((pair: LangPair) => (lang === 'ms' ? pair.ms : pair.en), [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLang(): LangContextType {
  const context = useContext(LangContext);
  if (context === undefined) {
    throw new Error('useLang must be used within a LangProvider');
  }
  return context;
}
